import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import {
  useUpdateMutation,
  useUser,
  useDropdown,
  UsecitiesDropdowns,
  useStatesDropdowns,
} from "../../actions/authActions";

// Helper function to create image from URL
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

// Helper function to get cropped image
async function getCroppedImg(imageSrc, pixelCrop, cropType = "default") {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true, alpha: true });

  // Determine export size based on crop type
  let exportWidth = pixelCrop.width;
  let exportHeight = pixelCrop.height;

  // Profile image - square
  if (cropType === "profile") {
    exportWidth = 430;
    exportHeight = 575;
  }

  // Vote to Win image - 650x866
  if (cropType === "voteToWin") {
    exportWidth = 531;
    exportHeight = 715;
  }

  canvas.width = exportWidth;
  canvas.height = exportHeight;

  // Clear and draw cropped area at correct scale
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    exportWidth,
    exportHeight
  );

  // Preserve PNG transparency if applicable
  const mimeType = imageSrc.startsWith("data:image/png")
    ? "image/png"
    : "image/jpeg";

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, 0.95); // 95% quality for JPEG
  });
}

export default () => {
  
  const navigate = useNavigate();
  const [loadingToapi, setLoadingToapi] = useState(false);
  const [countryCode, setCountryCode] = useState(null);
  const [cca2, setCca2] = useState(null);
  const userId = localStorage.getItem("userId");
  const [files, setFiles] = useState({
    image: null,
    voteToWinImage: null,
  });
  const [isDragging, setIsDragging] = useState({
    image: null,
    voteToWinImage: null,
  }); // null, 'image', or 'votowin'
  
  // Cropper state
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperType, setCropperType] = useState(null); // 'profile' or 'voteToWin'
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  
  const dispatch = useDispatch();
  const {
    data: userprofile,
    isLoading: isLoadingprefilled,
    isError,
    error,
  } = useUser(userId);
  
  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      first_name: userprofile?.first_name || "",
      last_name: userprofile?.last_name || "",
      mobile_num: userprofile?.mobile_num || "",
      email: userprofile?.email || "",
      gender_id: userprofile?.gender_id || "",
      date_of_birth: userprofile?.date_of_birth || "",
      image: userprofile?.image || "",
      city_id: userprofile?.city_id ? String(userprofile?.city_id) : "",
      country_id: userprofile?.country_id
        ? String(userprofile?.country_id)
        : "",
      state_id: userprofile?.state_id ? String(userprofile?.state_id) : "",
      agree_to_vote_to_win: userprofile?.agree_to_vote_to_win === 1 || "",
      referral_code: userprofile?.referral_code || "",
      usernameType: "", // Initialize username type
    },
  });

  useEffect(() => {
    console.log('═══════════════════════════════════');
    console.log('🔍 GENDER DEBUG INFO:');
    console.log('currentGenderId:', currentGenderId);
    console.log('Type:', typeof currentGenderId);
    console.log('Parsed:', parseInt(currentGenderId));
    console.log('userprofile.gender_id:', userprofile?.gender_id);
    console.log('Is Female (1)?:', parseInt(currentGenderId) === 1);
    console.log('Is Male (2)?:', parseInt(currentGenderId) === 2); 
    console.log('═══════════════════════════════════');
    if (
      !isLoadingprefilled &&
      userprofile &&
      Object.keys(userprofile).length > 0
    ) {
      
      setValue("first_name", userprofile?.first_name || "");
      setValue("last_name", userprofile?.last_name || "");
      setValue("email", userprofile?.email || "");
      setValue("mobile_num", userprofile?.mobile_num || "");
      
      // ✅ NEW: Determine username type based on user profile data
      // If user has email, set to "email", otherwise set to "phone"
      const determinedUsernameType = userprofile?.email && userprofile.email.trim() !== "" 
        ? "email" 
        : "phone";
      setValue("usernameType", determinedUsernameType);
      console.log("✅ Setting usernameType to:", determinedUsernameType);
      
      if (userprofile.date_of_birth) {
        const parsedDate = new Date(userprofile.date_of_birth);
        if (!isNaN(parsedDate)) {
          setValue("dob", parsedDate);
        }
      }
      
      // ✅ Fix: Convert gender_id to string to match dropdown option values
      const genderValue = userprofile?.gender_id ? String(userprofile?.gender_id) : "";
      console.log("✅ Setting Gender ID to:", genderValue);
      setValue("gender_id", genderValue); 
    
      setValue(
        "city_id",
        userprofile?.city_id ? String(userprofile?.city_id) : ""
      );
      setValue(
        "country_id",
        userprofile?.country_id ? String(userprofile?.country_id) : ""
      );
      setValue(
        "state_id",
        userprofile?.state_id ? String(userprofile?.state_id) : ""
      );
      setValue("agree_to_vote_to_win", userprofile?.agree_to_vote_to_win === 1);
      setValue("referral_code", userprofile?.referral_code);
      // setValue("image", userprofile?.image || '');
    }
  }, [userprofile, isLoadingprefilled, setValue]);
  const { data: dropdowns, isLoading } = useDropdown();
  
  // ✅ Debug: Log dropdown gender data when it loads
  useEffect(() => {
    if (dropdowns?.gender) {
      console.log("📊 DEBUG - Dropdown Gender Data:", dropdowns.gender);
      console.log("📊 DEBUG - Gender Options:");
      dropdowns.gender.forEach((g, index) => {
        console.log(`  [${index}] ID: ${g.id}, gender_id: ${g.gender_id} (${typeof g.gender_id}), gender: ${g.gender}`);
      });
    }
  }, [dropdowns]);
  
  const selectedCountry = watch("country_id");
  const selectedState = watch("state_id");
  const currentGenderId = watch("gender_id");
  const usernameType = watch("usernameType"); // ✅ Watch username type
  
  // ✅ Debug: Log current gender_id value
  useEffect(() => {
    console.log("🎯 Current gender_id value:", currentGenderId, "(Type:", typeof currentGenderId, ")");
  }, [currentGenderId]);
  const { data: states, isLoading: isLoadingStates } =
    useStatesDropdowns(selectedCountry);
  const { data: cities, isLoading: citiesisLoading } =
    UsecitiesDropdowns(selectedState);

  const mutation = useUpdateMutation(
    userId,
    dispatch,
    setLoadingToapi,
    navigate
  );

  // Cropper callback
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Handle file selection - open cropper
  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    if (selectedFiles.length > 0) {
      const file = selectedFiles[0];
      
      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert("Image size must be less than 2MB");
        e.target.value = '';
        return;
      }
      
      // Determine crop type based on field name
      const type = name === 'image' ? 'profile' : 'voteToWin';
      
      // Read file and open cropper
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setCropperType(type);
        setCropperOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle crop save
  const handleCropSave = async () => {
    try {
      if (!imageToCrop || !croppedAreaPixels || !cropperType) return;
      
      // Get cropped image blob
      const croppedBlob = await getCroppedImg(
        imageToCrop,
        croppedAreaPixels,
        cropperType
      );
      
      // Convert blob to File
      const fileName = cropperType === 'profile' 
        ? `profile_${Date.now()}.jpg`
        : `vote_to_win_${Date.now()}.jpg`;
        
      const croppedFile = new File(
        [croppedBlob],
        fileName,
        { type: "image/jpeg" }
      );
      
      // Store in files state
      const fieldName = cropperType === 'profile' ? 'image' : 'voteToWinImage';
      setFiles((prevFiles) => ({
        ...prevFiles,
        [fieldName]: croppedFile,
      }));
      
      // Close cropper
      setCropperOpen(false);
      setImageToCrop(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropperType(null);
    } catch (error) {
      console.error("Error cropping image:", error);
      alert("Failed to crop image. Please try again.");
    }
  };

  // Handle crop cancel
  const handleCropCancel = () => {
    setCropperOpen(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCropperType(null);
  };

  // Get aspect ratio based on crop type
  const getCropperAspectRatio = () => {
    switch (cropperType) {
      case 'profile':
        return 430 / 575;
      case 'voteToWin':
        return 530 / 715;
      default:
        return 1;
    }
  };

  // Get cropper title based on type
  const getCropperTitle = () => {
    switch (cropperType) {
      case 'profile':
        return 'Crop Profile Image (430×575 px)';
      case 'voteToWin':
        return 'Crop Vote To Win Image (530×715 px)';
      default:
        return 'Crop Image';
    }
  };

  const clearFileChange = (name) => {
    setFiles((prevFiles) => ({
      ...prevFiles,
      [name]: null,
    }));
  };
  
  const handleDrop = (e, fieldName) => {
    e.preventDefault();
    setIsDragging((prev) => ({ ...prev, [fieldName]: false }));

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type.startsWith("image/")) {
      // Validate file size
      if (droppedFile.size > 2 * 1024 * 1024) {
        alert("Image size must be less than 2MB");
        return;
      }
      
      // Determine crop type
      const type = fieldName === 'image' ? 'profile' : 'voteToWin';
      
      // Read file and open cropper
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setCropperType(type);
        setCropperOpen(true);
      };
      reader.readAsDataURL(droppedFile);
    }
  };

  // ✅ UPDATED: Clear email when username type changes to phone
  useEffect(() => {
    if (usernameType === "phone") {
      setValue("email", "");
    }
  }, [usernameType, setValue]);

  const handleDragEnter = (fieldName) => {
    setIsDragging((prev) => ({ ...prev, [fieldName]: true }));
  };
  const handleDragLeave = (fieldName) => {
    setIsDragging((prev) => ({ ...prev, [fieldName]: false }));
  };

  const isVoteToWinRequired = watch("agree_to_vote_to_win");
  const isVoteToWinImageSelected =
    Boolean(files.voteToWinImage) || Boolean(userprofile?.vote_to_win_image);

  if (isError) {
    return (
      <div className="flex items-center justify-center w-full">
        <p className="text-secondary">
          Error loading userprofile: {error.message}
        </p>
      </div>
    );
  }

  const onSubmit = (data, e) => {
    // console.log("🚀 PROFILE UPDATE SUBMISSION STARTED");
    // console.log("📋 Form Data:", data);
    
    e.preventDefault();
    setLoadingToapi(true);
    const playloadData = new FormData();
    
    // Extract dial code and phone number
    let phoneNumber = data.mobile_num || userprofile?.mobile_num || "";
    let dialCode = countryCode || userprofile?.country_code;
    // console.log("Original userprofile:", userprofile);
    
    let countryCode2 = cca2 || userprofile?.cca2;
    
    // console.log("🔍 Original userprofile: BEFORE PHONE PROCESSING:");
    // console.log("Original mobile_num:", phoneNumber);
    // console.log("Original countryCode:", dialCode);
    // console.log("Original cca2:", countryCode2);
    
    // If phone number contains a '+' at the start, extract the dial code
    if (phoneNumber && phoneNumber.startsWith('+')) {
      const match = phoneNumber.match(/^(\+\d+)\s*(.+)$/);
      if (match) {
        dialCode = match[1]; // e.g., "+92"
        phoneNumber = match[2]; // e.g., "3192973923"
      }
    }
    
    console.log("✅ AFTER PHONE PROCESSING:");
    console.log("Cleaned phoneNumber:", phoneNumber);
    console.log("Extracted dialCode:", dialCode);
    
    // ✅ UPDATED: Only append email if username type is email
    if (data.usernameType === "email") {
      playloadData.append("email", data.email || userprofile?.email);
    } else {
      playloadData.append("email", ""); // Send empty email for phone type
    }
    
    playloadData.append(
      "first_name",
      data.first_name || userprofile?.first_name
    );
    playloadData.append("last_name", data.last_name || userprofile?.last_name);
    playloadData.append("mobile_num", phoneNumber);
    playloadData.append("country_code", dialCode);
    playloadData.append("cca2", countryCode2);
    if (files.image) {
      playloadData.append("image", files.image);
    }
    if (files.voteToWinImage) {
      playloadData.append("vote_to_win_image", files.voteToWinImage);
    }
    playloadData.append(
      "country_id",
      data.country_id || userprofile?.country_id
    );
    playloadData.append("state_id", data.state_id || userprofile?.state_id);
    playloadData.append("city_id", data.city_id || userprofile?.city_id);
    const formattedDob = data.dob
      ? new Date(data.dob).toISOString().split("T")[0]
      : userprofile?.date_of_birth || "";
    playloadData.append("date_of_birth", formattedDob);
    playloadData.append(
      "gender_id",
      data.gender_id ? Number(data.gender_id) : Number(userprofile?.gender_id)
    );
    playloadData.append("terms_of_use", true);
    playloadData.append("_method", "PUT");
    playloadData.append(
      "agree_to_vote_to_win",
      data.agree_to_vote_to_win ? 1 : 0
    );
    // if (data.referral_code && data.referral_code.trim() !== "") {
    //   playloadData.append("referral_code", data.referral_code);
    // } else if (userprofile?.referral_code) {
    //   playloadData.append("referral_code", "");
    // }

    console.log("=== FINAL FormData Contents ===");
    for (let [key, value] of playloadData.entries()) {
      if (value instanceof File) {
        console.log(key, `File: ${value.name} (${value.size} bytes)`);
      } else {
        console.log(key, value);
      }
    }
    console.log("================================");

    mutation.mutate(playloadData);
  };
  return {
    navigate,
    userprofile,
    files,
    handleFileChange,
    clearFileChange,
    handleSubmit,
    onSubmit,
    control,
    errors,
    register,
    setValue,
    setCountryCode,
    setCca2,
    dropdowns,
    isLoadingprefilled,
    isLoading,
    states,
    isLoadingStates,
    cities,
    citiesisLoading,
    isDragging,
    handleDrop,
    handleDragEnter,
    handleDragLeave,
    isVoteToWinRequired,
    isVoteToWinImageSelected,
    loadingToapi,
    // Cropper related
    cropperOpen,
    cropperType,
    imageToCrop,
    crop,
    zoom,
    setCrop,
    setZoom,
    onCropComplete,
    handleCropSave,
    handleCropCancel,
    getCropperAspectRatio,
    getCropperTitle,
    currentGenderId,
    usernameType, // ✅ Export username type
  };
};